// src/pages/LandingPage.tsx
import React, { useState, useEffect } from 'react';
import { Sun, Moon, Upload, Activity, CheckCircle, Lock, Shield, Menu, X } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { LoginPage } from './auth/LoginPage';
import { RegisterPage } from './auth/RegisterPage';
import { ForgotPasswordPage } from './auth/ForgotPasswordPage';
import { TermsModal } from '../components/legal/TermsModal';

interface LandingPageProps {
  onLogin: (credentials: any) => Promise<void>;
  onRegister: (data: any) => Promise<void>;
  onForgotPassword: (email: string) => Promise<void>;
  onSeeSample?: () => void;
}

type AuthView = 'login' | 'register' | 'forgot-password';
type ModalType = 'about' | 'features' | 'team' | 'contact' | 'terms' | null;
type ContentModalType = 'about' | 'features' | 'team' | 'contact';

export const LandingPage: React.FC<LandingPageProps> = ({
  onLogin,
  onRegister,
  onForgotPassword,
  onSeeSample
}) => {
  const { theme, toggleTheme } = useTheme();
  const [currentAuthView, setCurrentAuthView] = useState<AuthView>('login');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const [termsModalTab, setTermsModalTab] = useState<'terms' | 'privacy'>('terms');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const totalSlides = 3;

  // Auto-rotate carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % totalSlides);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  };

  const previousSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  const openModal = (type: ModalType) => {
    setActiveModal(type);
  };

  const closeModal = () => {
    setActiveModal(null);
  };

  const modalContent: Record<ContentModalType, { title: string; text?: string; content?: React.ReactNode }> = {
    about: {
      title: 'About heal',
      content: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', color: 'var(--color-secondary)' }}>
            <span>
              heal organizes your <strong>lab reports and prescriptions</strong> — so they’re not scattered across WhatsApp, email, or paper files, but available in one secure place.  There’s more to heal but it’s better experienced than explained. Sign up and click upload 
            <Upload size={22} style={{ marginLeft: '0.4rem', verticalAlign: 'middle' }} />

            <span>.</span>
            </span>
            <span>
              Don’t worry — a sample report will be waiting inside so you can try heal right away.
            </span>

          </div>
      )
    },
    features: {
      title: 'Features',
      content: (
        <div style={{ lineHeight: '1.8', color: 'var(--color-secondary)' }}>
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ color: 'var(--color-primary)', fontSize: '1.3rem', marginBottom: '1rem', fontWeight: 600 }}>
              Available Now
            </h3>
            <p style={{ marginBottom: '0.75rem' }}>
              <strong style={{ color: 'var(--color-primary)' }}>Report Upload & Analysis</strong><br />
              <span style={{ fontSize: '1.15rem' }}>Upload health reports (PDF, images) and get instant AI extraction of your health metrics</span>
            </p>
            <p style={{ marginBottom: '0.75rem' }}>
              <strong style={{ color: 'var(--color-primary)' }}>Health Dashboard</strong><br />
              <span style={{ fontSize: '1.15rem' }}>Track 19+ health metrics across cardiology, blood tests, kidney function, liver, and more</span>
            </p>
            <p style={{ marginBottom: '0.75rem' }}>
              <strong style={{ color: 'var(--color-primary)' }}>Historical Tracking</strong><br />
              <span style={{ fontSize: '1.15rem' }}>View your health metrics over time with beautiful visualizations and trend analysis</span>
            </p>
            <p style={{ marginBottom: '0.75rem' }}>
              <strong style={{ color: 'var(--color-primary)' }}>Category Organization</strong><br />
              <span style={{ fontSize: '1.15rem' }}>Metrics automatically grouped by health category (heart, blood, kidney, liver, etc.)</span>
            </p>
            <p style={{ marginBottom: '0.75rem' }}>
              <strong style={{ color: 'var(--color-primary)' }}>Multi-language Support</strong><br />
              <span style={{ fontSize: '1.15rem' }}>Get your health summaries in Hindi or Malayalam</span>
            </p>
          </div>

          <div>
            <h3 style={{ color: '#6b7280', fontSize: '1.3rem', marginBottom: '1rem', fontWeight: 600 }}>
              Coming Soon
            </h3>
            <p style={{ marginBottom: '0.75rem', opacity: 0.8 }}>
              <strong style={{ color: 'var(--color-primary)' }}>Chat Interface</strong><br />
              <span style={{ fontSize: '1.15rem' }}>Talk to your AI health companion about your concerns and get instant insights</span>
            </p>
            <p style={{ marginBottom: '0.75rem', opacity: 0.8 }}>
              <strong style={{ color: 'var(--color-primary)' }}>My Brain</strong><br />
              <span style={{ fontSize: '1.15rem' }}>AI that remembers your complete health history for personalized insights</span>
            </p>
            <p style={{ marginBottom: '0.75rem', opacity: 0.8 }}>
              <strong style={{ color: 'var(--color-primary)' }}>Food & Body Image Analysis</strong><br />
              <span style={{ fontSize: '1.15rem' }}>Upload photos of food or body images for instant health analysis</span>
            </p>
            <p style={{ marginBottom: '0.75rem', opacity: 0.8 }}>
              <strong style={{ color: 'var(--color-primary)' }}>Vaccine & Medical History</strong><br />
              <span style={{ fontSize: '1.15rem' }}>Complete medical history tracking including vaccines, surgeries, and medications</span>
            </p>
            <p style={{ opacity: 0.8 }}>
              <strong style={{ color: 'var(--color-primary)' }}>Full Multi-language Support</strong><br />
              <span style={{ fontSize: '1.15rem' }}>Use the entire app in Hindi or Malayalam</span>
            </p>
          </div>
        </div>
      )
    },
    team: {
      title: 'Team',
      content: (
        <div style={{ lineHeight: '1.8', color: 'var(--color-secondary)', fontSize: '1.15rem' }}>
          <p style={{ marginBottom: '1.5rem' }}>
            Hi, I'm <strong style={{ color: 'var(--color-primary)' }}>Prateek</strong> — the founder, developer, designer, and support staff behind heal.
          </p>
          <p style={{ marginBottom: '1.5rem' }}>
            This app is completely bootstrapped from my bedroom in Mumbai. Every line of code, every design decision, every bug fix — it's all me. I built heal because your lab reports and prescriptions deserve to be at your fingertips — not scattered across emails, WhatsApp chats, and dusty folders.
          </p>
          <p style={{ marginBottom: '1.5rem' }}>
            Being a one-person team means I can move fast, listen closely, and build exactly what you need. It also means when you reach out, you're talking directly to the person who built this.
          </p>
          <p>
            Questions, feedback, or just want to say hi? Write to me at{' '}
            <a href="mailto:help@healapp.co.in" style={{ color: 'var(--color-primary)', textDecoration: 'underline' }}>
              help@healapp.co.in
            </a>
            {' '}— I personally read and respond to every message.
          </p>
        </div>
      )
    },
    contact: {
      title: 'Contact',
      content: (
        <div style={{ lineHeight: '1.8', color: 'var(--color-secondary)', fontSize: '1.15rem' }}>
          <p style={{ marginBottom: '1.5rem' }}>
            Got a question? Found a bug? Have an idea? I'd love to hear from you.
          </p>

          <p style={{ marginBottom: '1.5rem' }}>
            <strong style={{ color: 'var(--color-primary)' }}>Email:</strong>{' '}
            <a href="mailto:help@healapp.co.in" style={{ color: 'var(--color-primary)', textDecoration: 'underline' }}>
              help@healapp.co.in
            </a>
          </p>

          <p style={{ marginBottom: '1.5rem' }}>
            <strong style={{ color: 'var(--color-primary)' }}>In-App Feedback:</strong> Once you're signed in, you can drop feedback directly from the app.
          </p>

          <p style={{ fontSize: '1rem', opacity: 0.85 }}>
            Every message goes straight to the founder — no ticket queues.
          </p>
        </div>
      )
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--color-background)',
      color: 'var(--color-text)',
      fontFamily: 'var(--font-primary)',
      position: 'relative'
    }}>
      {/* Top Navigation */}
      <nav className="glass landing-nav" style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        padding: '1rem 2rem',
        paddingTop: 'max(1rem, env(safe-area-inset-top))',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '2rem',
        zIndex: 1000,
        minHeight: '60px'
      }}>
        {/* Mobile Menu Button */}
        <button
          className="mobile-menu-btn"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          style={{
            display: 'none',
            position: 'absolute',
            left: '1rem',
            background: 'transparent',
            border: 'none',
            color: 'var(--color-primary)',
            cursor: 'pointer',
            padding: '0.5rem',
            zIndex: 1001
          }}
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

        <div className="nav-links" style={{ display: 'flex', gap: '2rem', alignItems: 'center' }}>
          <button
            onClick={() => { openModal('about'); setMobileMenuOpen(false); }}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--color-secondary)',
              fontSize: '1.1rem',
              fontWeight: 500,
              cursor: 'pointer',
              padding: '0.5rem 1rem',
              transition: 'all 0.3s ease',
              fontFamily: 'inherit'
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = 'var(--color-primary)'}
            onMouseLeave={(e) => e.currentTarget.style.color = 'var(--color-secondary)'}
          >
            About
          </button>
          <button
            onClick={() => { openModal('features'); setMobileMenuOpen(false); }}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--color-secondary)',
              fontSize: '1.1rem',
              fontWeight: 500,
              cursor: 'pointer',
              padding: '0.5rem 1rem',
              transition: 'all 0.3s ease',
              fontFamily: 'inherit'
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = 'var(--color-primary)'}
            onMouseLeave={(e) => e.currentTarget.style.color = 'var(--color-secondary)'}
          >
            Features
          </button>
          <button
            onClick={() => { openModal('team'); setMobileMenuOpen(false); }}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--color-secondary)',
              fontSize: '1.1rem',
              fontWeight: 500,
              cursor: 'pointer',
              padding: '0.5rem 1rem',
              transition: 'all 0.3s ease',
              fontFamily: 'inherit'
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = 'var(--color-primary)'}
            onMouseLeave={(e) => e.currentTarget.style.color = 'var(--color-secondary)'}
          >
            Team
          </button>
          <button
            onClick={() => { openModal('contact'); setMobileMenuOpen(false); }}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--color-secondary)',
              fontSize: '1.1rem',
              fontWeight: 500,
              cursor: 'pointer',
              padding: '0.5rem 1rem',
              transition: 'all 0.3s ease',
              fontFamily: 'inherit'
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = 'var(--color-primary)'}
            onMouseLeave={(e) => e.currentTarget.style.color = 'var(--color-secondary)'}
          >
            Contact
          </button>
        </div>

        {/* Theme Toggle */}
        <button
          onClick={toggleTheme}
          className="theme-toggle"
          style={{
            position: 'absolute',
            right: '2rem',
            top: '50%',
            transform: 'translateY(-50%)',
            background: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
            border: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'}`,
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            color: 'var(--color-primary)',
            flexShrink: 0
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = theme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
          }}
          aria-label="Toggle theme"
        >
          {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
        </button>
      </nav>

      {/* Main Container */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '40% 60%',
        minHeight: '100vh',
        paddingTop: '70px'
      }} className="landing-container">
        {/* Left Pane - Authentication */}
        <div style={{
          background: 'var(--color-background)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'center',
          padding: '3rem 2.5rem',
          paddingTop: '4rem',
          borderRight: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.03)'}`
        }} className="landing-left-pane">
          <div style={{ width: '100%', maxWidth: '400px' }}>
            {currentAuthView === 'login' && (
              <div>
                <div style={{
                  fontSize: '2.5rem',
                  fontWeight: 700,
                  letterSpacing: '-0.05em',
                  marginBottom: '3rem',
                  color: 'var(--color-primary)',
                  textAlign: 'center'
                }}>
                  heal
                </div>
                <LoginPage
                  embedded={true}
                  onLogin={onLogin}
                  onNavigateToRegister={() => setCurrentAuthView('register')}
                  onNavigateToForgotPassword={() => setCurrentAuthView('forgot-password')}
                />
              </div>
            )}

            {currentAuthView === 'register' && (
              <div>
                <div style={{
                  fontSize: '2.5rem',
                  fontWeight: 700,
                  letterSpacing: '-0.05em',
                  marginBottom: '3rem',
                  color: 'var(--color-primary)',
                  textAlign: 'center'
                }}>
                  heal
                </div>
                <RegisterPage
                  embedded={true}
                  onRegister={onRegister}
                  onNavigateToLogin={() => setCurrentAuthView('login')}
                />
              </div>
            )}

            {currentAuthView === 'forgot-password' && (
              <div>
                <div style={{
                  fontSize: '2.5rem',
                  fontWeight: 700,
                  letterSpacing: '-0.05em',
                  marginBottom: '3rem',
                  color: 'var(--color-primary)',
                  textAlign: 'center'
                }}>
                  heal
                </div>
                <ForgotPasswordPage
                  embedded={true}
                  onSubmit={onForgotPassword}
                  onNavigateToLogin={() => setCurrentAuthView('login')}
                />
              </div>
            )}
          </div>
        </div>

        {/* Right Pane - Features Carousel */}
        <div style={{
          background: 'var(--color-background)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '2rem 2.5rem',
          position: 'relative',
          overflow: 'hidden'
        }} className="landing-right-pane">
          {/* Animated Background Elements */}
          <div className="animate-float" style={{
            position: 'absolute',
            top: '10%',
            right: '10%',
            width: '150px',
            height: '150px',
            background: 'radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%)',
            borderRadius: '50%',
            filter: 'blur(40px)',
            zIndex: 0
          }} />
          <div className="animate-float" style={{
            position: 'absolute',
            bottom: '15%',
            left: '5%',
            width: '200px',
            height: '200px',
            background: 'radial-gradient(circle, rgba(168, 85, 247, 0.1) 0%, transparent 70%)',
            borderRadius: '50%',
            filter: 'blur(60px)',
            zIndex: 0,
            animationDelay: '1s'
          }} />

          <div style={{ width: '100%', maxWidth: '700px' }}>
            {/* Hero Section */}
            <div className="hero-section" style={{ textAlign: 'center', marginBottom: '3rem', position: 'relative', zIndex: 10 }}>
              <h1 className="hero-title" style={{
                fontSize: '3.5rem',
                fontWeight: 800,
                color: 'var(--color-primary)',
                marginBottom: '1rem',
                lineHeight: 1.1,
                letterSpacing: '-0.02em'
              }}>
                Your Health,<br />
                <span className="text-gradient">Decoded in Seconds.</span>
              </h1>

              <p className="hero-subtitle" style={{
                fontSize: '1.25rem',
                color: 'var(--color-secondary)',
                maxWidth: '600px',
                margin: '0 auto 2rem auto'
              }}>
                Stop guessing. Upload your lab reports and prescriptions to get insights and clear tracking in one place.
              </p>


            </div>

            {/* 3-Step Guide */}
            <div className="steps-grid" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '1.5rem',
              marginBottom: '3rem'
            }}>
              {[
                {
                  step: '01',
                  title: 'Upload',
                  desc: 'Drop any lab report or prescription (PDF/Image).',
                  icon: <Upload size={20} />
                },
                {
                  step: '02',
                  title: 'Extract',
                  desc: 'Our AI identifies metrics and trends automatically.',
                  icon: <Activity size={20} />
                },
                {
                  step: '03',
                  title: 'Analyze',
                  desc: 'Get a clear summary and actionable insights.',
                  icon: <CheckCircle size={20} />
                }
              ].map((item, i) => (
                <div key={i} className="step-card glass" style={{
                  padding: '2rem',
                  borderRadius: '24px',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  cursor: 'default',
                  zIndex: 1
                }}>
                  <div style={{
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    color: '#3b82f6',
                    marginBottom: '0.75rem',
                    letterSpacing: '0.1em'
                  }}>
                    STEP {item.step}
                  </div>
                  <h3 style={{
                    fontSize: '1.25rem',
                    fontWeight: 700,
                    color: 'var(--color-primary)',
                    marginBottom: '0.75rem'
                  }}>
                    {item.title}
                  </h3>
                  <p style={{
                    fontSize: '0.925rem',
                    color: 'var(--color-secondary)',
                    lineHeight: 1.6
                  }}>
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>

            {/* Carousel Wrapper */}
            <div className="carousel-wrapper" style={{
              position: 'relative',
              overflow: 'hidden',
              marginBottom: '1.5rem',
              minHeight: '500px'
            }}>
              <div style={{
                display: 'flex',
                transform: `translateX(-${currentSlide * 100}%)`,
                transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
              }}>
                {/* Slide 1: Chat Interface */}
                <div className="carousel-slide" style={{
                  minWidth: '100%',
                  padding: '1.5rem 2rem',
                  textAlign: 'center'
                }}>
                  <div className="carousel-image-container" style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginBottom: '1.5rem',
                    height: '350px'
                  }}>
                    <img
                      src="/images/score_&_ai_analysis.png"
                      alt="Health score and AI analysis"
                      style={{
                        maxWidth: '80%',
                        maxHeight: '100%',
                        width: 'auto',
                        height: 'auto',
                        objectFit: 'contain',
                        borderRadius: '12px',
                        boxShadow: theme === 'dark'
                          ? '0 8px 32px rgba(0, 0, 0, 0.5)'
                          : '0 8px 32px rgba(0, 0, 0, 0.1)'
                      }}
                    />
                  </div>
                  <h2 className="carousel-title" style={{
                    fontSize: '2rem',
                    fontWeight: 700,
                    color: 'var(--color-primary)',
                    marginBottom: '1.25rem'
                  }}>
                    upload. see clarity.
                  </h2>
                  <p style={{
                    fontSize: '1.125rem',
                    color: 'var(--color-secondary)',
                    lineHeight: 1.7,
                    maxWidth: '550px',
                    margin: '0 auto'
                  }}>
                    your reports, turned into clear insights in seconds.
                  </p>
                </div>

                {/* Slide 2: Health Dashboard */}
                <div className="carousel-slide" style={{
                  minWidth: '100%',
                  padding: '1.5rem 2rem',
                  textAlign: 'center'
                }}>
                  <div className="carousel-image-container" style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginBottom: '1.5rem',
                    height: '350px'
                  }}>
                    <img
                      src="/images/trend_over_time.png"
                      alt="Health metrics trend over time"
                      style={{
                        maxWidth: '80%',
                        maxHeight: '100%',
                        width: 'auto',
                        height: 'auto',
                        objectFit: 'contain',
                        borderRadius: '12px',
                        boxShadow: theme === 'dark'
                          ? '0 8px 32px rgba(0, 0, 0, 0.5)'
                          : '0 8px 32px rgba(0, 0, 0, 0.1)'
                      }}
                    />
                  </div>
                  <h2 className="carousel-title" style={{
                    fontSize: '2rem',
                    fontWeight: 700,
                    color: 'var(--color-primary)',
                    marginBottom: '1.25rem'
                  }}>
                    your health history, clearly
                  </h2>
                  <p style={{
                    fontSize: '1.125rem',
                    color: 'var(--color-secondary)',
                    lineHeight: 1.7,
                    maxWidth: '550px',
                    margin: '0 auto'
                  }}>
                    track your health across months and years, not isolated results
                  </p>
                </div>

                {/* Slide 3: My Brain */}
                <div className="carousel-slide" style={{
                  minWidth: '100%',
                  padding: '1.5rem 2rem',
                  textAlign: 'center'
                }}>
                  <div className="carousel-image-container" style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginBottom: '1.5rem',
                    height: '350px'
                  }}>
                    <img
                      src="/images/Cbc.png"
                      alt="Medical term decoding example"
                      style={{
                        maxWidth: '50%',
                        maxHeight: '100%',
                        width: 'auto',
                        height: 'auto',
                        objectFit: 'contain',
                        borderRadius: '12px',
                        boxShadow: theme === 'dark'
                          ? '0 8px 32px rgba(0, 0, 0, 0.5)'
                          : '0 8px 32px rgba(0, 0, 0, 0.1)'
                      }}
                    />
                  </div>
                  <h2 className="carousel-title" style={{
                    fontSize: '2rem',
                    fontWeight: 700,
                    color: 'var(--color-primary)',
                    marginBottom: '1.25rem'
                  }}>
                    let heal do the decoding
                  </h2>
                  <p style={{
                    fontSize: '1.125rem',
                    color: 'var(--color-secondary)',
                    lineHeight: 1.7,
                    maxWidth: '550px',
                    margin: '0 auto'
                  }}>
                    results summarized and grouped by health category — no decoding required
                  </p>
                </div>
              </div>
            </div>

            {/* Navigation Arrows */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '1rem',
              marginTop: '1rem'
            }}>
              <button
                onClick={previousSlide}
                style={{
                  background: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                  border: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'}`,
                  color: 'var(--color-primary)',
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  fontSize: '1.5rem'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = theme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
                }}
                aria-label="Previous slide"
              >
                ‹
              </button>
              <button
                onClick={nextSlide}
                style={{
                  background: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                  border: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'}`,
                  color: 'var(--color-primary)',
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  fontSize: '1.5rem'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = theme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
                }}
                aria-label="Next slide"
              >
                ›
              </button>
            </div>
            {/* Trust Section */}
            <div className="trust-section" style={{
              marginTop: '4rem',
              padding: '2rem',
              background: theme === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
              borderRadius: '32px',
              border: '1px solid var(--color-border)',
              textAlign: 'center'
            }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: 'var(--color-primary)', marginBottom: '1.5rem' }}>
                Why trust heal?
              </h3>
              <div className="trust-items" style={{ display: 'flex', justifyContent: 'center', gap: '3rem', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <Lock size={20} className="text-gradient" />
                  <span style={{ fontSize: '0.95rem', color: 'var(--color-secondary)' }}>Bank-Grade Security</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <Shield size={20} className="text-gradient" />
                  <span style={{ fontSize: '0.95rem', color: 'var(--color-secondary)' }}>Privacy First</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <Activity size={20} className="text-gradient" />
                  <span style={{ fontSize: '0.95rem', color: 'var(--color-secondary)' }}>Smart Extraction</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Simple Modal for About, Features, Team, Contact */}
      {activeModal && (activeModal === 'about' || activeModal === 'features' || activeModal === 'team' || activeModal === 'contact') && (
        <div
          className="modal-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(10px)',
            zIndex: 2000,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '2rem'
          }}
          onClick={closeModal}
        >
          <div
            className="modal-content"
            style={{
              background: theme === 'dark' ? '#1a1a1a' : '#ffffff',
              border: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'}`,
              borderRadius: '16px',
              padding: '3rem',
              maxWidth: '700px',
              width: '100%',
              maxHeight: 'calc(100vh - 4rem)',
              position: 'relative',
              animation: 'modalSlideIn 0.3s ease',
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeModal}
              className="modal-close-btn"
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                background: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                border: 'none',
                color: 'var(--color-secondary)',
                fontSize: '1.25rem',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
                flexShrink: 0
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = 'var(--color-primary)';
                e.currentTarget.style.background = theme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'var(--color-secondary)';
                e.currentTarget.style.background = theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)';
              }}
            >
              ✕
            </button>
            <h2 style={{
              fontSize: '2rem',
              fontWeight: 700,
              color: 'var(--color-primary)',
              marginBottom: '1.5rem',
              paddingRight: '3rem',
              flexShrink: 0
            }}>
              {modalContent[activeModal]?.title}
            </h2>
            <div style={{
              fontSize: '1.15rem',
              lineHeight: 1.8,
              overflowY: 'auto',
              flex: 1,
              paddingRight: '0.5rem'
            }}>
              {modalContent[activeModal]?.content || (
                <p style={{ color: 'var(--color-secondary)', lineHeight: 1.8, fontSize: '1.15rem' }}>
                  {modalContent[activeModal]?.text}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Terms & Privacy Modal */}
      {activeModal === 'terms' && (
        <TermsModal
          isOpen={true}
          onClose={closeModal}
          initialTab={termsModalTab}
        />
      )}

      {/* Responsive Styles */}
      <style>{`
        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .step-card:hover {
          transform: translateY(-10px);
          border-color: #3b82f6;
          box-shadow: 0 12px 30px rgba(59, 130, 246, 0.15);
        }

        @media (max-width: 1024px) {
          .landing-container {
            grid-template-columns: 45% 55% !important;
          }

          /* Modal Tablet */
          .modal-content {
            max-width: 600px !important;
            padding: 2.5rem !important;
          }
        }

        @media (max-width: 768px) {
          .landing-container {
            grid-template-columns: 1fr !important;
          }

          .landing-left-pane {
            border-right: none !important;
            border-bottom: 1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.03)'} !important;
            padding: 2rem 1.5rem !important;
          }

          .landing-right-pane {
            padding: 2.5rem 1.5rem !important;
          }

          /* Mobile Navigation */
          .mobile-menu-btn {
            display: flex !important;
            position: relative !important;
            left: auto !important;
            top: auto !important;
            flex-shrink: 0;
          }

          .landing-nav {
            padding: 0.5rem 1rem !important;
            padding-top: max(0.5rem, env(safe-area-inset-top)) !important;
            min-height: 56px !important;
            justify-content: space-between !important;
          }

          .nav-links {
            position: fixed;
            top: calc(56px + env(safe-area-inset-top, 0px));
            left: 0;
            right: 0;
            background: ${theme === 'dark' ? 'rgba(0, 0, 0, 0.98)' : 'rgba(255, 255, 255, 0.98)'};
            backdrop-filter: blur(10px);
            flex-direction: column;
            padding: 1rem 1.5rem;
            gap: 0.25rem !important;
            transform: translateY(${mobileMenuOpen ? '0' : '-120%'});
            opacity: ${mobileMenuOpen ? '1' : '0'};
            pointer-events: ${mobileMenuOpen ? 'auto' : 'none'};
            transition: all 0.3s ease;
            border-bottom: 1px solid var(--color-border);
            z-index: 1000;
          }

          .nav-links button {
            width: 100%;
            text-align: center;
            padding: 1rem !important;
            font-size: 1rem !important;
          }

          .theme-toggle {
            position: relative !important;
            right: auto !important;
            top: auto !important;
            transform: none !important;
            width: 36px !important;
            height: 36px !important;
            flex-shrink: 0;
          }

          /* Hero Section Mobile */
          .hero-title {
            font-size: 2rem !important;
          }

          .hero-subtitle {
            font-size: 1rem !important;
            padding: 0 0.5rem;
          }

          /* Step Cards Mobile */
          .steps-grid {
            grid-template-columns: 1fr !important;
            gap: 1rem !important;
          }

          .step-card {
            padding: 1.5rem !important;
          }

          .step-card:hover {
            transform: none;
          }

          /* Carousel Mobile */
          .carousel-wrapper {
            min-height: 380px !important;
          }

          .carousel-slide {
            padding: 1rem !important;
          }

          .carousel-image-container {
            height: 200px !important;
          }

          .carousel-image-container img {
            max-width: 90% !important;
          }

          .carousel-title {
            font-size: 1.5rem !important;
          }

          /* Trust Section Mobile */
          .trust-section {
            margin-top: 2rem !important;
            padding: 1.5rem 1rem !important;
            border-radius: 20px !important;
          }

          .trust-items {
            gap: 1.5rem !important;
            flex-direction: column;
            align-items: center;
          }

          /* Modal Mobile - Centered & Cross-Device */
          .modal-overlay {
            padding: 1rem !important;
            align-items: center !important;
          }

          .modal-content {
            padding: 1.5rem !important;
            padding-top: 2.5rem !important;
            max-height: 85vh !important;
            max-width: 95% !important;
            width: 100% !important;
            overflow-y: auto;
            border-radius: 16px !important;
            -webkit-overflow-scrolling: touch;
          }

          .modal-content h2 {
            font-size: 1.4rem !important;
            margin-bottom: 1rem !important;
            padding-right: 2rem !important;
          }

          .modal-content > div {
            font-size: 1rem !important;
            line-height: 1.6 !important;
          }

          .modal-content h3 {
            font-size: 1.1rem !important;
          }

          .modal-content p {
            font-size: 0.95rem !important;
            margin-bottom: 0.5rem !important;
          }

          .modal-content p span {
            font-size: 0.95rem !important;
          }

          .modal-content button[style*="position: absolute"] {
            top: 1rem !important;
            right: 1rem !important;
            width: 32px !important;
            height: 32px !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
          }
        }

        /* Extra small screens */
        @media (max-width: 480px) {
          .landing-nav {
            padding: 0.4rem 0.75rem !important;
          }

          .theme-toggle {
            width: 32px !important;
            height: 32px !important;
          }

          .hero-title {
            font-size: 1.75rem !important;
          }

          .hero-subtitle {
            font-size: 0.95rem !important;
          }

          .carousel-wrapper {
            min-height: 340px !important;
          }

          .carousel-image-container {
            height: 160px !important;
          }

          .carousel-title {
            font-size: 1.25rem !important;
          }

          .step-card {
            padding: 1.25rem !important;
          }

          /* Modal Extra Small - Centered */
          .modal-overlay {
            padding: 0.75rem !important;
          }

          .modal-content {
            padding: 1.25rem !important;
            padding-top: 2.25rem !important;
            max-height: 88vh !important;
            border-radius: 14px !important;
          }

          .modal-content h2 {
            font-size: 1.2rem !important;
          }

          .modal-content h3 {
            font-size: 1rem !important;
            margin-bottom: 0.75rem !important;
          }

          .modal-content p {
            font-size: 0.9rem !important;
          }

          .modal-content button[style*="position: absolute"] {
            top: 0.75rem !important;
            right: 0.75rem !important;
            width: 28px !important;
            height: 28px !important;
            font-size: 1.25rem !important;
          }
        }
      `}</style>
    </div>
  );
};
